import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarBlank, ChatCircle, DeviceMobile, GoogleLogo, Image } from '@phosphor-icons/react';
import { mapAuthError } from '@mybusinessmate/api-client';
import { RowValue, SettingCard, SettingRow } from '../../../../../components/settings/setting-card';
import { ValueEditorDialog } from '../../../../../components/settings/value-editor-dialog';
import { Button } from '../../../../../components/ui/button';
import { StatusTag } from '../../../../../components/ui/status-tag';
import { Toggle } from '../../../../../components/ui/toggle';
import { authClient } from '../../../../../lib/api';
import { useSession } from '../../../../../lib/session';
import { describeUserAgent, formatActivity } from '../../../../../lib/session-format';
import { Skeleton } from '../../../../../components/ui/skeleton';
import { useStaffPreferences } from '../../../../../lib/workspace-api';

export const Route = createFileRoute('/subdomain/admin/_staff/settings/connections')({
  component: ConnectionsPage,
});

function ConnectionsPage() {
  const { data: session } = useSession();
  const user = session?.user;
  const { data: prefs, save } = useStaffPreferences('connections');
  const [editing, setEditing] = useState(false);

  const accounts = useQuery({
    queryKey: ['auth', 'accounts'] as const,
    queryFn: async () => {
      const { data, error } = await authClient.listAccounts();
      if (error) throw mapAuthError(error);
      return data ?? [];
    },
  });
  const sessions = useQuery({
    queryKey: ['auth', 'sessions'] as const,
    queryFn: async () => {
      const { data, error } = await authClient.listSessions();
      if (error) throw mapAuthError(error);
      return data ?? [];
    },
  });

  const google = accounts.data?.find((a) => a.providerId === 'google');
  const phone = sessions.data?.find((s) => /iphone|android/i.test(s.userAgent ?? ''));
  if (!prefs) return <Skeleton className="h-(--mbm-layout-dialog-wide-width) rounded-(--mbm-radius-lg)" />;
  const chatName = prefs.chatName || user?.name.split(/\s+/)[0] || '';

  return (
    <>
      <SettingCard title="Google" subtitle="Used to sign in and to put meetings you host on your calendar.">
        <SettingRow
          icon={GoogleLogo}
          title="Google sign-in"
          subtitle={google ? user?.email : 'Not connected'}
          trailing={<StatusTag label={google ? 'Connected' : 'Not connected'} tone={google ? 'green' : 'grey'} dot />}
        />
        {/* TODO(api): Google Calendar access (and when it expires) has no endpoint yet. */}
        <SettingRow
          icon={CalendarBlank}
          title="Google Calendar"
          subtitle="Calendar sync isn’t available yet."
          trailing={
            <Button variant="secondary" size="row" disabled>
              Connect
            </Button>
          }
        />
      </SettingCard>

      <SettingCard title="Chat" subtitle="How you show up in client and team chats.">
        <SettingRow
          icon={ChatCircle}
          title="Name in chat"
          subtitle="Clients see this next to your messages"
          trailing={<RowValue>{chatName}</RowValue>}
          onClick={() => setEditing(true)}
        />
        <SettingRow
          icon={Image}
          title="Use my profile photo in chat"
          subtitle="Turn off to show your initials instead"
          trailing={
            <Toggle
              aria-label="Use my profile photo in chat"
              checked={prefs.useProfilePhoto}
              onCheckedChange={(on) => save.mutate({ ...prefs, useProfilePhoto: on })}
            />
          }
        />
      </SettingCard>

      <SettingCard title="Phone app" subtitle="Get push alerts and reply to clients on the go.">
        {phone ? (
          <SettingRow
            icon={DeviceMobile}
            title={describeUserAgent(phone.userAgent)}
            subtitle={`Push alerts on · last opened ${formatActivity(phone.updatedAt)}`}
            trailing={<StatusTag label="Connected" tone="green" dot />}
          />
        ) : (
          <SettingRow
            icon={DeviceMobile}
            muted
            title="No phone connected yet"
            subtitle="Sign in to the MyBusinessMate app with your work email."
          />
        )}
      </SettingCard>

      <ValueEditorDialog
        open={editing}
        onClose={() => setEditing(false)}
        title="Name in chat"
        description="Clients see this next to your messages."
        label="Name"
        editor={{ kind: 'text', value: chatName, maxLength: 40 }}
        onSave={(value) => save.mutate({ ...prefs, chatName: String(value) })}
      />
    </>
  );
}
